import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Grid, Select, MenuItem, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import MDInput from "components/MDInput";
import { get, postFile, postNoResponse } from "utils/WebService";
import moment from "moment";
import { MySwal } from "App";
import { useMaterialUIController } from "context";

// Situações do boleto (SituacaoBoletoEnum)
const SITUACOES_BOLETO = [
    { value: 1, label: "Emitido (Aguardando Envio)" },
    { value: 2, label: "Aguardando Registro" },
    { value: 3, label: "Registrado" },
    { value: 4, label: "Rejeitado" },
    { value: 5, label: "Pago" },
    { value: 6, label: "Aguardando Cancelamento / Baixa" },
    { value: 7, label: "Cancelado" },
];
const geraRemessa = (situacao) => situacao === 1 || situacao === 6;

const escaparHtml = (texto) => String(texto ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function GerarRemessa() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [contas, setContas] = useState([]);
    const [selectedConta, setSelectedConta] = useState("");
    const [selectedSituacao, setSelectedSituacao] = useState(1);
    const [boletos, setBoletos] = useState([]);
    const [selectedBoletos, setSelectedBoletos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pastaRemessa, setPastaRemessa] = useState("");
    // "Alterar situação": boletos escolhidos (ids), nova situação e motivo
    const [troca, setTroca] = useState(null);

    useEffect(() => {
        carregarContas();
    }, []);

    // Pasta onde a remessa do banco da conta é gravada (Parametrização do Sistema > Pastas e Arquivos)
    useEffect(() => {
        if (!selectedConta) {
            setPastaRemessa("");
            return;
        }
        get(`/cnab/pastas?contaBancariaId=${encodeURIComponent(selectedConta)}`)
            .then(res => setPastaRemessa(res?.remessa || ""))
            .catch(() => setPastaRemessa(""));
    }, [selectedConta]);

    const carregarContas = () => {
        get('/conta-bancaria/list-all').then(res => {
            if (res && res.length > 0) {
                setContas(res);
                setSelectedConta(res[0].id);
                buscarBoletos(res[0].id, selectedSituacao);
            } else {
                buscarBoletos("", selectedSituacao);
            }
        }).catch(err => {
            console.error(err);
            buscarBoletos("", selectedSituacao);
        });
    };

    const handleContaChange = (e) => {
        const idConta = e.target.value;
        setSelectedConta(idConta);
        if (idConta) {
            buscarBoletos(idConta, selectedSituacao);
        } else {
            setBoletos([]);
        }
    };

    const handleSituacaoChange = (e) => {
        const situacao = e.target.value;
        setSelectedSituacao(situacao);
        if (selectedConta) {
            buscarBoletos(selectedConta, situacao);
        }
    };

    const buscarBoletos = (idConta, situacao) => {
        setLoading(true);
        get(`/cnab/boletos-pendentes?contaBancariaId=${idConta || ''}&situacao=${situacao}`)
            .then(res => {
                setBoletos(res || []);
                setSelectedBoletos([]);
            })
            .catch(err => {
                console.error(err);
                setBoletos([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleRefresh = () => {
        if (selectedConta) {
            buscarBoletos(selectedConta, selectedSituacao);
        }
    };

    const handleGerarRemessa = () => {
        if (!selectedConta) {
            MySwal.fire('Aviso', 'Selecione uma conta bancária primeiro.', 'warning');
            return;
        }

        if (selectedBoletos.length === 0) {
            MySwal.fire('Aviso', 'Selecione ao menos um boleto para gerar a remessa.', 'warning');
            return;
        }

        setLoading(true);
        // A remessa é gravada na pasta da conta (sem conseguir, nada é gerado): o postFile não a baixa (res.salvoEm)
        postFile('/cnab/gerar-remessa', {
            contaBancariaId: selectedConta,
            boletosIds: selectedBoletos,
            situacaoBoleto: selectedSituacao
        })
            .then((res) => {
                const onde = res?.salvoEm || `${pastaRemessa || "pasta da conta"} (subpasta do ano/mês)`;
                MySwal.fire({ title: 'Sucesso', icon: 'success', html: `Arquivo de remessa gerado e gravado em:<br/><b>${escaparHtml(onde)}</b>` });
                buscarBoletos(selectedConta, selectedSituacao);
            })
            .catch(err => {
                console.error(err);
                MySwal.fire('Erro', err?.message || 'Erro ao gerar arquivo de remessa.', 'error');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleAlterarSituacao = (idBoleto, novaSituacao, label) => {
        MySwal.fire({
            title: `Marcar como ${label}?`,
            text: `Deseja alterar a situação do boleto #${idBoleto} para ${label}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sim, alterar',
            cancelButtonText: 'Não'
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                postNoResponse("/cnab/alterar-situacao", { idBoleto, situacao: novaSituacao })
                    .then(() => {
                        MySwal.fire('Sucesso', `Boleto marcado como ${label}!`, 'success');
                        buscarBoletos(selectedConta, selectedSituacao);
                    })
                    .catch(err => {
                        console.error(err);
                        MySwal.fire('Erro', err.message || 'Erro ao alterar situação do boleto.', 'error');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    };

    const abrirTroca = (ids) => setTroca({ ids, situacao: "", motivo: "" });

    const confirmarTroca = () => {
        if (!troca?.situacao) {
            MySwal.fire('Aviso', 'Escolha a nova situação.', 'warning');
            return;
        }
        if (!troca.motivo.trim()) {
            MySwal.fire('Aviso', 'Informe o motivo: ele fica registrado na ocorrência da ficha.', 'warning');
            return;
        }
        setLoading(true);
        postNoResponse("/cnab/alterar-situacao", { ids: troca.ids, situacao: troca.situacao, motivo: troca.motivo.trim() })
            .then(() => {
                const rotulo = SITUACOES_BOLETO.find(o => o.value === troca.situacao)?.label;
                setTroca(null);
                MySwal.fire('Sucesso', `${troca.ids.length === 1 ? "Boleto alterado" : `${troca.ids.length} boletos alterados`} para ${rotulo}.`, 'success');
                buscarBoletos(selectedConta, selectedSituacao);
            })
            .catch(err => {
                console.error(err);
                MySwal.fire('Erro', err.message || 'Erro ao alterar a situação do boleto.', 'error');
            })
            .finally(() => setLoading(false));
    };

    const handleCancelarBoleto = (idParcela) => {
        MySwal.fire({
            title: 'Cancelar Boleto?',
            text: "O boleto será marcado para cancelamento/baixa e precisará ser enviado na próxima remessa ao banco.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, cancelar',
            cancelButtonText: 'Não'
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                postNoResponse("/acordo/parcela/boleto/cancelar", { idParcela })
                    .then(() => {
                        MySwal.fire('Sucesso', 'Boleto cancelado com sucesso!', 'success');
                        buscarBoletos(selectedConta, selectedSituacao);
                    })
                    .catch(err => {
                        console.error(err);
                        MySwal.fire('Erro', err.message || 'Erro ao cancelar boleto.', 'error');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = boletos.map((n) => n.idBoleto);
            setSelectedBoletos(newSelecteds);
            return;
        }
        setSelectedBoletos([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selectedBoletos.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedBoletos, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedBoletos.slice(1));
        } else if (selectedIndex === selectedBoletos.length - 1) {
            newSelected = newSelected.concat(selectedBoletos.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedBoletos.slice(0, selectedIndex),
                selectedBoletos.slice(selectedIndex + 1),
            );
        }
        setSelectedBoletos(newSelected);
    };

    const isSelected = (id) => selectedBoletos.indexOf(id) !== -1;

    const selectSx = {}; // aparência dos campos vem do tema (assets/coresTema)

    return (
        <MDBox>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Conta Bancária</InputLabel>
                    <Select
                        id="conta-select"
                        value={selectedConta}
                        onChange={handleContaChange}
                        size="small"
                        fullWidth
                        displayEmpty
                        sx={selectSx}
                    >
                        <MenuItem value="" disabled><em>Selecione...</em></MenuItem>
                        {contas.map(conta => (
                            <MenuItem key={conta.id} value={conta.id}>
                                {conta.banco} - Ag: {conta.agencia} C/C: {conta.conta} ({conta.descricao})
                            </MenuItem>
                        ))}
                    </Select>
                    {pastaRemessa && (
                        <MDTypography variant="caption" color={darkMode ? "white" : "text"} sx={{ px: 1, pt: 0.5, display: "block", wordBreak: "break-all" }}>
                            Arquivo gravado em: {pastaRemessa} (subpasta do ano/mês; não é baixado no computador). Altere em Parametrização do Sistema &gt; Pastas e Arquivos.
                        </MDTypography>
                    )}
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Situação do Boleto</InputLabel>
                    <Select
                        id="situacao-select"
                        value={selectedSituacao}
                        onChange={handleSituacaoChange}
                        size="small"
                        fullWidth
                        sx={selectSx}
                    >
                        <MenuItem value={1}>Aguardando Envio (Emitidos)</MenuItem>
                        <MenuItem value={6}>Aguardando Cancelamento / Baixa</MenuItem>
                        <MenuItem value={2}>Enviados para Registro</MenuItem>
                        <MenuItem value={3}>Registrados</MenuItem>
                        <MenuItem value={4}>Rejeitados</MenuItem>
                        <MenuItem value={5}>Pagos</MenuItem>
                        <MenuItem value={7}>Cancelados</MenuItem>
                    </Select>
                </Grid>

                <Grid item xs={12} md={4} display="flex" alignItems="flex-end" gap={2}>
                    <MDButton 
                        variant="gradient" 
                        color="info" 
                        onClick={handleGerarRemessa}
                        sx={{ height: "30px" }}
                        disabled={selectedBoletos.length === 0 || loading || !geraRemessa(selectedSituacao)}
                        title={!geraRemessa(selectedSituacao) ? "Só é possível gerar remessa para boletos Aguardando Envio ou Baixa" : ""}
                    >
                        GERAR REMESSA ({geraRemessa(selectedSituacao) ? selectedBoletos.length : 0})
                    </MDButton>

                    <MDButton
                        variant="outlined"
                        color="info"
                        onClick={() => abrirTroca(selectedBoletos)}
                        sx={{ height: "30px" }}
                        disabled={selectedBoletos.length === 0 || loading}
                        title="Muda só a situação dos boletos selecionados no sistema (não envia nada ao banco)"
                    >
                        ALTERAR SITUAÇÃO ({selectedBoletos.length})
                    </MDButton>

                    <MDButton 
                        variant="outlined" 
                        color={darkMode ? "white" : "dark"} 
                        onClick={handleRefresh}
                        sx={{ height: "30px" }}
                        disabled={loading || !selectedConta}
                        title="Atualizar lista"
                    >
                        ATUALIZAR
                    </MDButton>
                </Grid>

                <Grid item xs={12}>
                    <MDBox width="100%">
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }} size="small" aria-label="boletos table">
                                <TableHead sx={{ display: "table-header-group" }}>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                indeterminate={selectedBoletos.length > 0 && selectedBoletos.length < boletos.length}
                                                checked={boletos.length > 0 && selectedBoletos.length === boletos.length}
                                                onChange={handleSelectAllClick}
                                                sx={{ color: darkMode ? "white" : "inherit" }}
                                            />
                                        </TableCell>
                                        <TableCell><MDTypography variant="caption" color={darkMode ? "white" : "secondary"} fontWeight="medium" textTransform="uppercase">ID</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color={darkMode ? "white" : "secondary"} fontWeight="medium" textTransform="uppercase">Nosso Número</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color={darkMode ? "white" : "secondary"} fontWeight="medium" textTransform="uppercase">Devedor</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color={darkMode ? "white" : "secondary"} fontWeight="medium" textTransform="uppercase">CPF/CNPJ</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color={darkMode ? "white" : "secondary"} fontWeight="medium" textTransform="uppercase">Vencimento</MDTypography></TableCell>
                                        <TableCell align="right"><MDTypography variant="caption" color={darkMode ? "white" : "secondary"} fontWeight="medium" textTransform="uppercase">Valor</MDTypography></TableCell>
                                        <TableCell align="center"><MDTypography variant="caption" color={darkMode ? "white" : "secondary"} fontWeight="medium" textTransform="uppercase">Ações</MDTypography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {boletos.map((row) => {
                                        const isItemSelected = isSelected(row.idBoleto);
                                        return (
                                            <TableRow
                                                hover
                                                onClick={(event) => handleClick(event, row.idBoleto)}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.idBoleto}
                                                selected={isItemSelected}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox color="primary" checked={isItemSelected} sx={{ color: darkMode ? "white" : "inherit" }} />
                                                </TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>{row.idBoleto}</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>{row.nossoNumero}</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>{row.nomeDevedor}</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>{row.cpfCnpjDevedor}</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>{row.dataVencimento ? moment(row.dataVencimento).format("DD/MM/YYYY") : ""}</MDTypography></TableCell>
                                                <TableCell align="right"><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>{row.valor ? `R$ ${row.valor.toFixed(2)}` : ""}</MDTypography></TableCell>
                                                <TableCell align="center">
                                                     <MDBox display="flex" justifyContent="center" gap={0.5}>
                                                         {(selectedSituacao === 1 || selectedSituacao === 2 || selectedSituacao === 4) && (
                                                             <MDButton 
                                                                 variant="text" 
                                                                 color="success" 
                                                                 size="small" 
                                                                 onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handleAlterarSituacao(row.idBoleto, 3, "Aprovado / Registrado");
                                                                 }}
                                                             >
                                                                 Aprovar
                                                             </MDButton>
                                                         )}
                                                         {(selectedSituacao === 1 || selectedSituacao === 2 || selectedSituacao === 3) && (
                                                             <MDButton 
                                                                 variant="text" 
                                                                 color="warning" 
                                                                 size="small" 
                                                                 onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handleAlterarSituacao(row.idBoleto, 4, "Rejeitado");
                                                                 }}
                                                             >
                                                                 Rejeitar
                                                             </MDButton>
                                                         )}
                                                         {(selectedSituacao === 1 || selectedSituacao === 2 || selectedSituacao === 3) && (
                                                             <MDButton 
                                                                 variant="text" 
                                                                 color="error" 
                                                                 size="small" 
                                                                 onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handleCancelarBoleto(row.idParcela);
                                                                 }}
                                                             >
                                                                 Cancelar
                                                             </MDButton>
                                                         )}
                                                         <MDButton
                                                             variant="text"
                                                             color="info"
                                                             size="small"
                                                             title="Mudar a situação deste boleto (só no sistema)"
                                                             onClick={(e) => {
                                                                 e.stopPropagation();
                                                                 abrirTroca([row.idBoleto]);
                                                             }}
                                                         >
                                                             Situação
                                                         </MDButton>
                                                         {selectedSituacao === 4 && (
                                                             <MDButton 
                                                                 variant="text" 
                                                                 color="info" 
                                                                 size="small" 
                                                                 onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handleAlterarSituacao(row.idBoleto, 1, "Aguardando Envio");
                                                                 }}
                                                             >
                                                                 Reenviar
                                                             </MDButton>
                                                         )}
                                                     </MDBox>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {boletos.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "#7b809a", py: 3 }}>
                                                Nenhum boleto pendente para a conta selecionada.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </MDBox>
                </Grid>
            </Grid>

            <Dialog open={!!troca} onClose={() => !loading && setTroca(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Alterar situação {troca?.ids?.length > 1 ? `de ${troca.ids.length} boletos` : `do boleto #${troca?.ids?.[0] ?? ""}`}
                </DialogTitle>
                <DialogContent>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
                        Muda só a situação no sistema: não baixa nem estorna a parcela e não envia nada ao banco. Cada
                        boleto alterado ganha uma ocorrência na ficha com a situação anterior, a nova, quem alterou e o motivo.
                    </MDTypography>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nova situação</InputLabel>
                    <Select
                        value={troca?.situacao ?? ""}
                        onChange={(e) => setTroca(t => ({ ...t, situacao: e.target.value }))}
                        size="small"
                        fullWidth
                        displayEmpty
                    >
                        <MenuItem value="" disabled><em>Selecione...</em></MenuItem>
                        {SITUACOES_BOLETO.map(o => (
                            <MenuItem key={o.value} value={o.value} disabled={o.value === selectedSituacao}>{o.label}</MenuItem>
                        ))}
                    </Select>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 2 }}>Motivo</InputLabel>
                    <MDInput
                        size="small"
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Ex.: pago no banco (consta como liquidado no Itaú); baixa confirmada pelo banco..."
                        value={troca?.motivo ?? ""}
                        onChange={(e) => setTroca(t => ({ ...t, motivo: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <MDButton variant="outlined" color="secondary" size="small" onClick={() => setTroca(null)} disabled={loading}>
                        Cancelar
                    </MDButton>
                    <MDButton variant="gradient" color="info" size="small" onClick={confirmarTroca} disabled={loading}>
                        Alterar
                    </MDButton>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}

export default GerarRemessa;
